// src/components/DashboardHome.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
// --- MODIFICA: Rimosso useOutletContext ---
import { Link as RouterLink } from 'react-router-dom';
// --- FINE MODIFICA ---
import { format } from 'date-fns';
import { useTranslation, Trans } from 'react-i18next'; 
import {
  Box, Typography, CircularProgress, Paper, Alert, List, 
  ListItem, ListItemButton, ListItemIcon, ListItemText,
  LinearProgress, Chip,
  Button,
  Grid
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ListAltIcon from '@mui/icons-material/ListAlt'; 
import CampaignIcon from '@mui/icons-material/Campaign';
import QuickLifingWidget from './QuickLifingWidget';
import EngineeringIcon from '@mui/icons-material/Engineering';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import TimerIcon from '@mui/icons-material/Timer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

// ── Widget meteo radar (Windy embed) ─────────────────────────────
function WeatherWidget({ lat, lon, trackName, t }) {
  if (!lat || !lon) return null;
  const src = `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lon}&detailLat=${lat}&detailLon=${lon}&width=650&height=380&pois=false&level=surface&overlay=rain&product=ecmwf&menu=&message=true&marker=true&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=km%2Fh&metricTemp=%C2%B0C&radarRange=-1`;
  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', backgroundColor: 'background.paper', height: '100%' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <WbSunnyIcon sx={{ color: 'warning.main', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700}>{t('weather_radar_title')}{trackName ? ` — ${trackName}` : ''}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{t('weather_powered_by')}</Typography>
        </Box>
        <Box sx={{ position: 'relative', width: '100%', height: { xs: 260, sm: 380 } }}>
          <iframe
            src={src}
            title="Meteo Radar"
            style={{ border: 'none', width: '100%', height: '100%', display: 'block' }}
            allow="geolocation"
          />
        </Box>
      </Paper>
  );
}

// ── Widget live timing (iframe con URL configurabile) ─────────────
function LiveTimingWidget({ url, eventName, t }) {
  const [iframeError, setIframeError] = useState(false);
  if (!url) return null;
  return (
      <Paper elevation={0} sx={{ border: '2px solid', borderColor: 'secondary.main', overflow: 'hidden', backgroundColor: 'background.paper', height: '100%' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <TimerIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} color="secondary.main">
            {t('live_timing_title')}{eventName ? ` — ${eventName}` : ''}
          </Typography>
          <Tooltip title={t('live_timing_open_tab')}>
            <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ ml: 'auto' }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {iframeError ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('live_timing_no_embed')}
            </Typography>
            <Button variant="outlined" size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />}>
              {t('live_timing_open')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', width: '100%', height: { xs: 260, sm: 380 } }}>
            <iframe
              src={url}
              title="Live Timing"
              style={{ border: 'none', width: '100%', height: '100%', display: 'block' }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              onError={() => setIframeError(true)}
            />
          </Box>
        )}
      </Paper>
  );
}

// --- WIDGETS (Invariati) ---

const NextSessionWidget = ({ sessions, t, teamDetails }) => { 
  const [, setTick] = useState(0);
  useEffect(() => { const timer = setInterval(() => setTick(prev => prev + 1), 1000); return () => clearInterval(timer); }, []);
  
  if (!sessions || sessions.length === 0) { return <Typography>{t('timer_no_sessions')}</Typography>; } 
  
  const now = Timestamp.now();
  const pitLaneBufferMin = teamDetails?.pitLaneBuffer || 0;
  const bufferSeconds = pitLaneBufferMin * 60;
  
  let nextSession = null, currentSession = null;
  
  for (const session of sessions) {
    const effectiveStartTime = new Timestamp(session.startTime.seconds - bufferSeconds, session.startTime.nanoseconds);
    if (effectiveStartTime > now) { 
      nextSession = session; 
      break; 
    }
    if (session.startTime && session.duration) { 
      const originalEndTimeSeconds = session.startTime.seconds + session.duration * 60;
      const effectiveEndTime = new Timestamp(originalEndTimeSeconds + bufferSeconds, session.startTime.nanoseconds); 
      if (effectiveStartTime <= now && effectiveEndTime > now) {
        currentSession = session; 
        break; 
      }
    }
  }

  if (currentSession) { 
    const originalStartTime = currentSession.startTime;
    const originalEndTimeSeconds = currentSession.startTime.seconds + currentSession.duration * 60;
    const originalEndTime = new Timestamp(originalEndTimeSeconds, currentSession.startTime.nanoseconds);
    
    if (now.seconds < originalStartTime.seconds) {
        const secondsToStart = originalStartTime.seconds - now.seconds;
        const minutes = Math.floor(secondsToStart / 60);
        const seconds = secondsToStart % 60;
        
        return (
          <Box> 
            <Typography variant="h4" color="error.main">{t('timer_pitlane_open')}</Typography> 
            <Typography variant="h5" gutterBottom>{currentSession.sessionName} ({currentSession.eventName})</Typography> 
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('timer_session_starts_in')} {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
            </Typography>
            <Typography variant="body2" color="text.secondary">{t('timer_session_duration', { duration: currentSession.duration })}</Typography>
          </Box> 
        );
    }
    
    const totalSeconds = originalEndTime.seconds - now.seconds; 
    
    if (totalSeconds < 0) {
        return ( 
          <Box> 
            <Typography variant="h4" color="error.main">{t('timer_in_progress')}</Typography> 
            <Typography variant="h5" gutterBottom>{currentSession.sessionName} ({currentSession.eventName})</Typography> 
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t('timer_session_ended_pit_open')}
            </Typography>
          </Box> 
        ); 
    }
    
    const minutes = Math.floor(totalSeconds / 60); 
    const seconds = totalSeconds % 60; 
    
    return ( 
      <Box> 
        <Typography variant="h4" color="error.main">{t('timer_in_progress')}</Typography> 
        <Typography variant="h5" gutterBottom>{currentSession.sessionName} ({currentSession.eventName})</Typography> 
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('timer_remaining')} {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
        </Typography>
      </Box> 
    ); 
  } 

  if (nextSession) { 
    const effectiveNextStartTimeSeconds = nextSession.startTime.seconds - bufferSeconds;
    const totalSeconds = effectiveNextStartTimeSeconds - now.seconds; 
    
    const days = Math.floor(totalSeconds / (3600 * 24)); 
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600); 
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return ( 
      <Box> 
        <Typography variant="h4" color="primary.main">{t('timer_next_session')}</Typography> 
        <Typography variant="h5" gutterBottom>{nextSession.sessionName} ({nextSession.eventName})</Typography> 
        <Typography variant="h3" sx={{ mb: 2 }}>
          {t('timer_starts_in')} {days > 0 ? `${days}${t('timer_days_short')} ` : ''}{hours}{t('timer_hours_short')} {minutes}{t('timer_minutes_short')} {seconds < 10 ? `0${seconds}` : seconds}{t('timer_seconds_short')}
        </Typography> 
      </Box> 
    ); 
  } 
  
  return <Typography variant="h5">{t('timer_no_imminent')}</Typography>;
};

const ChecklistWarningsWidget = ({ sessions, setups, checklists, assignments, userRole, userId, t }) => { 
  const now = Timestamp.now(); const warnings = []; const userVehicleIds = assignments[userId] || [];
  for (const session of sessions) {
    if (session.startTime < now) {
      const setup = setups.find(s => s.sessionId === session.id); 
      if (setup) {
        if ((userRole === 'Mechanic' || userRole === 'Magazziniere') && !userVehicleIds.includes(setup.vehicleId)) { continue; }
        const checklist = checklists[setup.id];
        if (checklist) { const isComplete = checklist.items.length > 0 && checklist.items.every(item => item.checked); if (!isComplete) { warnings.push({ text: t('checklist_incomplete', { eventName: session.eventName, sessionName: session.sessionName }), vehicleId: setup.vehicleId, setupId: setup.id, session: session }); }
        } else { warnings.push({ text: t('checklist_not_attached', { eventName: session.eventName, sessionName: session.sessionName }), vehicleId: setup.vehicleId, setupId: setup.id, session: session }); }
      }
    }
  }
  if (warnings.length === 0) { return <Alert severity="success">{t('checklist_past_complete')}</Alert>; }
  warnings.sort((a, b) => b.session.startTime.seconds - a.session.startTime.seconds);
  return (
    <List dense disablePadding>
      {warnings.slice(0, 5).map((warning, index) => (
        <ListItem key={index} disablePadding>
          <ListItemButton component={RouterLink} to={`/vehicle/${warning.vehicleId}/setup/${warning.setupId}`}>
            <ListItemIcon sx={{ minWidth: 40 }}><WarningAmberIcon color="warning" /></ListItemIcon>
            <ListItemText primary={warning.text} secondary={`${t('session_date')} ${format(warning.session.startTime.toDate(), 'dd/MM/yyyy HH:mm')}`} /> 
          </ListItemButton>
        </ListItem> 
      ))}
    </List>
  );
};

const getUsageStatus = (percentage) => { if (percentage >= 100) return { color: 'error', level: 'critical' }; if (percentage >= 90) return { color: 'error', level: 'high' }; if (percentage >= 75) return { color: 'warning', level: 'medium' }; return { color: 'success', level: 'low' }; };
const LifingWarningsWidget = ({ components, vehicles, assignments, userRole, userId, t }) => { 
  const warnings = []; const userVehicleIds = assignments[userId] || [];
  for (const component of components) {
    const usagePercent = (component.totalLife > 0) ? (component.currentUsage / component.totalLife) * 100 : 0;
    const usageStatus = getUsageStatus(usagePercent);
    if (usageStatus.level !== 'low') {
      if ((userRole === 'Mechanic' || userRole === 'Magazziniere') && !userVehicleIds.includes(component.vehicleId)) { continue; }
      warnings.push({ ...component, usagePercent: usagePercent, vehicleName: vehicles[component.vehicleId] || t('unknown_vehicle'), status: usageStatus });
    }
  }
  if (warnings.length === 0) { return <Alert severity="success">{t('lifing_all_ok')}</Alert>; }
  warnings.sort((a, b) => b.usagePercent - a.usagePercent);
  return ( <List dense disablePadding> {warnings.slice(0, 5).map((warning) => ( <ListItem key={warning.id} disablePadding> <ListItemButton component={RouterLink} to={`/vehicle/${warning.vehicleId}?tab=3`}>
    <ListItemIcon sx={{ minWidth: 40 }}><WarningAmberIcon color={warning.status.color === 'success' ? 'warning' : warning.status.color} /></ListItemIcon> 
    <ListItemText primary={`${warning.name} (${warning.vehicleName})`} secondary={ <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5 }}> <LinearProgress variant="determinate" value={warning.usagePercent > 100 ? 100 : warning.usagePercent} color={warning.status.color === 'success' ? 'warning' : warning.status.color} sx={{ height: 6, borderRadius: 5, flexGrow: 1 }}/> <Chip label={`${warning.usagePercent.toFixed(1)}%`} color={warning.status.color === 'success' ? 'warning' : warning.status.color} size="small" variant="outlined"/> </Box> } /> </ListItemButton> </ListItem> ))} </List> );
};

const IssueLogWidget = ({ issues, vehicles, assignments, userRole, userId, t }) => { 
  const userVehicleIds = assignments[userId] || [];
  const openIssues = issues.filter(issue => { if ((userRole === 'Mechanic' || userRole === 'Magazziniere') && !userVehicleIds.includes(issue.vehicleId)) { return false; } return true; });
  if (openIssues.length === 0) { return <Alert severity="success">{t('issues_all_ok')}</Alert>; }
  openIssues.sort((a, b) => b.createdAt.seconds - b.createdAt.seconds);
  return ( <List dense disablePadding> {openIssues.slice(0, 5).map((issue) => ( <ListItem key={issue.id} disablePadding> <ListItemButton component={RouterLink} to={`/vehicle/${issue.vehicleId}?tab=2`}>
    <ListItemIcon sx={{ minWidth: 40 }}><BuildIcon color="error" /></ListItemIcon> <ListItemText primary={issue.description} secondary={`${t('issues_vehicle')} ${vehicles[issue.vehicleId] || t('unknown_vehicle')}`} /> 
  </ListItemButton> </ListItem> ))} </List> );
};

const UpcomingLogisticsWidget = ({ events, t }) => {
  const now = Timestamp.now();
  const upcomingEvents = events.filter(event => event.endDate && event.endDate > now);
  const warnings = [];
  for (const event of upcomingEvents) {
    const vehicleIds = event.assignedVehicleIds || [];
    const templateIds = event.assignedTemplateIds || [];
    if (vehicleIds.length === 0) { warnings.push({ type: 'vehicles', event: event }); }
    if (templateIds.length === 0) { warnings.push({ type: 'checklists', event: event }); }
  }
  if (warnings.length === 0) { return <Alert severity="success">{t('logistics_all_ok')}</Alert>; }
  warnings.sort((a, b) => a.event.startDate.seconds - b.event.startDate.seconds);
  return (
    <List dense disablePadding>
      {warnings.slice(0, 5).map((warning, index) => (
        <ListItem key={index} disablePadding>
          <ListItemButton component={RouterLink} to={`/event/${warning.event.id}`}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              {warning.type === 'vehicles' ? 
                <DirectionsCarIcon color="warning" /> : 
                <PlaylistAddCheckIcon color="warning" />}
            </ListItemIcon>
            <ListItemText 
              primary={warning.type === 'vehicles' ? 
                t('logistics_missing_vehicles', { eventName: warning.event.eventName }) :
                t('logistics_missing_checklists', { eventName: warning.event.eventName })
              }
              secondary={`${t('logistics_event_starts')} ${format(warning.event.startDate.toDate(), 'dd/MM/yyyy')}`} 
            /> 
          </ListItemButton>
        </ListItem> 
      ))}
    </List>
  );
};

const PendingEventChecklistsWidget = ({ checklists, eventsMap, t }) => {
  const now = Timestamp.now();
  const incompleteChecklists = checklists.filter(cl => !cl.isComplete);
  
  if (incompleteChecklists.length === 0) {
    return <Alert severity="success">{t('event_checklists_all_complete')}</Alert>;
  }

  const warnings = [];
  for (const checklist of incompleteChecklists) {
    const event = eventsMap[checklist.eventId];
    if (event && event.endDate > now) {
      warnings.push({
        checklist: checklist,
        event: event
      });
    }
  }

  if (warnings.length === 0) {
    return <Alert severity="success">{t('event_checklists_all_upcoming_complete')}</Alert>;
  }
  warnings.sort((a, b) => a.event.startDate.seconds - b.event.startDate.seconds);

  return (
    <List dense disablePadding>
      {warnings.slice(0, 5).map((warning, index) => (
        <ListItem key={warning.checklist.id || index} disablePadding>
          <ListItemButton 
            component={RouterLink} 
            to={`/vehicle/${warning.checklist.vehicleId}?tab=4&event=${warning.event.id}`}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ListAltIcon color="warning" />
            </ListItemIcon>
            <ListItemText 
              primary={`"${warning.checklist.templateName}" for ${warning.checklist.vehicleName}`}
              secondary={`${t('event_checklists_item_event')} ${warning.event.eventName} (${t('event_checklists_item_starts')} ${format(warning.event.startDate.toDate(), 'dd/MM/yyyy')})`}
            /> 
          </ListItemButton>
        </ListItem> 
      ))}
    </List>
  );
};

const PitLaneAccessWidget = ({ sessions, setups, vehiclesMap, userRole, userId, assignments, t, teamDetails }) => {
  const now = Timestamp.now();
  const pitLaneBufferMin = teamDetails?.pitLaneBuffer || 0;
  const bufferSeconds = pitLaneBufferMin * 60;

  let currentSession = null;
  for (const session of sessions) {
    if (session.startTime && session.duration) { 
      const effectiveStartTime = new Timestamp(session.startTime.seconds - bufferSeconds, session.startTime.nanoseconds);
      const originalEndTimeSeconds = session.startTime.seconds + session.duration * 60;
      const effectiveEndTime = new Timestamp(originalEndTimeSeconds + bufferSeconds, session.startTime.nanoseconds);
      
      if (effectiveStartTime <= now && effectiveEndTime > now) {
        currentSession = session;
        break; 
      }
    }
  }

  if (!currentSession) {
    return null;
  }

  const linkedSetups = setups.filter(s => s.sessionId === currentSession.id);
  if (linkedSetups.length === 0) {
    return null;
  }

  const isManager = userRole === 'admin' || userRole === 'Engineer';
  const userVehicleIds = assignments[userId] || [];
  
  const visibleSetups = linkedSetups.filter(setup => {
    if (isManager) return true;
    if (userRole === 'Mechanic') {
      return userVehicleIds.includes(setup.vehicleId);
    }
    return false;
  });

  if (visibleSetups.length === 0) {
    return null;
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: '1px solid', borderColor: 'error.main', backgroundColor: 'background.paper' }}
    >
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <EngineeringIcon sx={{ mr: 1 }} />
        {t('pit_access_title')}
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        <Trans 
          t={t}
          i18nKey="pit_access_subtitle"
          values={{ sessionName: currentSession.sessionName }}
          components={{ strong: <strong /> }}
        />
      </Typography>
      <Grid container spacing={2}>
        {visibleSetups.map(setup => (
          <Grid item xs={12} sm={6} md={4} key={setup.id}>
            <Button
              component={RouterLink}
              to={`/vehicle/${setup.vehicleId}/setup/${setup.id}`}
              variant="contained"
              color="error"
              size="large"
              fullWidth
              sx={{ height: '60px', flexDirection: 'column', textAlign: 'center' }}
            >
              <Typography variant="button" sx={{ fontWeight: 700 }}>{t('pit_access_button')}</Typography>
              <Typography variant="caption" sx={{ textTransform: 'none' }}>
                {vehiclesMap[setup.vehicleId] || t('pit_access_vehicle')} ({setup.nomePilota || t('pit_access_no_driver')})
              </Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

const UnreadFeedbackWidget = ({ feedbacks, vehiclesMap, userRole, assignments, userId, t }) => {
  const userVehicleIds = assignments[userId] || [];
  const visibleFeedbacks = feedbacks.filter(fb => {
    if (userRole === 'admin' || userRole === 'Engineer') return true;
    return userVehicleIds.includes(fb.vehicleId);
  });

  if (visibleFeedbacks.length === 0) return null;

  return (
    <List dense disablePadding>
      {visibleFeedbacks.slice(0, 5).map((fb) => (
        <ListItem key={fb.id} disablePadding>
          <ListItemButton component={RouterLink} to={`/vehicle/${fb.vehicleId}/setup/${fb.setupId}`}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <CampaignIcon color="secondary" />
            </ListItemIcon>
            <ListItemText 
              primary={`${t('unread_feedback_item', { defaultValue: 'Nuovo Feedback da' })} ${fb.driverName}`}
              secondary={`${vehiclesMap[fb.vehicleId] || t('unknown_vehicle')} - ${fb.sessionName}`}
            /> 
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

// --- FINE WIDGETS ---


// --- MODIFICA: Accetta 'mode' e 'teamLogos' come props ---
function DashboardHome({ teamId, userRole, userId, mode, teamLogos }) {
  const { t } = useTranslation('dashboard');
  // --- MODIFICA: Rimosso useOutletContext ---
  // const { mode, teamLogos } = useOutletContext(); 
  // --- FINE MODIFICA ---

  // Stati (invariati)
  const [allSessions, setAllSessions] = useState([]);
  const [allSetups, setAllSetups] = useState([]);
  const [allChecklists, setAllChecklists] = useState({});
  const [allAssignments, setAllAssignments] = useState({});
  const [allComponents, setAllComponents] = useState([]);
  const [vehiclesMap, setVehiclesMap] = useState({});
  const [allIssues, setAllIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [vehiclesList, setVehiclesList] = useState([]);
  const [teamDetails, setTeamDetails] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [allEventChecklists, setAllEventChecklists] = useState([]);
  const [allUnreadFeedbacks, setAllUnreadFeedbacks] = useState([]);
  const [currentEventTrack, setCurrentEventTrack] = useState(null); // { lat, lon, nome } 

  // useEffect SUPER-LOADER (invariato)
  useEffect(() => {
    if (!teamId) return;
    setIsLoading(true);
    const unsubscribers = [];
    const requiredLoads = 9; 
    let loadCount = 0;
    const checkAllLoaded = () => { loadCount++; if (loadCount >= requiredLoads) { setIsLoading(false); console.log("Dashboard: Data loaded."); } };
    
    const teamDocRef = doc(db, 'teams', teamId);
    const unsubTeamDetails = onSnapshot(teamDocRef, (docSnap) => {
        if (docSnap.exists()) { setTeamDetails(docSnap.data()); }
        checkAllLoaded();
    }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubTeamDetails);
    
    const eventsQuery = query(collection(db, "events"), where("teamId", "==", teamId));
    const unsubEvents = onSnapshot(eventsQuery, (eventsSnap) => {
        const promises = [];
        const eventsList = [];
        eventsSnap.forEach(eventDoc => { 
            const eventData = eventDoc.data();
            const eventId = eventDoc.id;
            eventsList.push({ id: eventId, ...eventData }); 
            const q = query(collection(db,"events", eventId,"sessions"),orderBy("startTime","asc")); 
            promises.push(getDocs(q).then(s=>s.docs.map(d=>({id:d.id,...d.data(),eventId:eventId,eventName:eventData.eventName})))); 
        });
        setAllEvents(eventsList); 
        Promise.all(promises).then(sessionsArrays => { 
            const flatList = [].concat(...sessionsArrays); 
            flatList.sort((a, b) => a.startTime.seconds - b.startTime.seconds); 
            setAllSessions(flatList); 
            checkAllLoaded(); 
        });
    }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubEvents);
    
    const setupsQuery = query(collection(db, "setupSheets"), where("teamId", "==", teamId));
    const unsubSetups = onSnapshot(setupsQuery, (sS) => { 
      const setupsList = sS.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllSetups(setupsList); 
      checkAllLoaded(); 
    }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubSetups);
    
    const checklistsQuery = query(collection(db, "setupChecklists"), where("teamId", "==", teamId));
    const unsubChecklists = onSnapshot(checklistsQuery, (cS) => { const map={}; cS.forEach(d=>{const c=d.data(); map[c.setupId]=c;}); setAllChecklists(map); checkAllLoaded(); }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubChecklists);
    
    const assignQuery = query(collection(db, "vehicleAssignments"), where("teamId", "==", teamId));
    const unsubAssignments = onSnapshot(assignQuery, (qs) => { const map={}; qs.forEach(d=>{const a=d.data(); if(!map[a.userId]){map[a.userId]=[];} map[a.userId].push(a.vehicleId);}); setAllAssignments(map); checkAllLoaded(); }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubAssignments);
    
    const componentsQuery = query(collection(db, "vehicleComponents"), where("teamId", "==", teamId));
    const unsubComponents = onSnapshot(componentsQuery, (qs) => { setAllComponents(qs.docs.map(d => ({ id: d.id, ...d.data() }))); checkAllLoaded(); }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubComponents);
    
    const vehiclesQuery = query(collection(db, "vehicles"), where("teamId", "==", teamId));
    const unsubVcl = onSnapshot(vehiclesQuery, (qs) => { 
      const map={}; 
      const list = [];
      qs.forEach(d=>{
        map[d.id]=`${d.data().marca} ${d.data().modello}`;
        list.push({ id: d.id, ...d.data() });
      });
      setVehiclesMap(map); 
      setVehiclesList(list);
      checkAllLoaded();
    }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubVcl);
    
    const issuesQuery = query(collection(db, "vehicleIssues"), where("teamId", "==", teamId), where("status", "==", "Aperto"));
    const unsubIssues = onSnapshot(issuesQuery, (qs) => { setAllIssues(qs.docs.map(d => ({ id: d.id, ...d.data() }))); checkAllLoaded(); }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubIssues);
    
    const eventChecklistsQuery = query(collection(db, "eventChecklists"), where("teamId", "==", teamId));
    const unsubEventChecklists = onSnapshot(eventChecklistsQuery, (qs) => {
      setAllEventChecklists(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      checkAllLoaded();
    }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubEventChecklists);
    
    const feedbackQuery = query(collection(db, "driverFeedback"), where("teamId", "==", teamId), where("isUnread", "==", true));
    const unsubFeedbacks = onSnapshot(feedbackQuery, (qs) => {
      setAllUnreadFeedbacks(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      checkAllLoaded();
    }, (err) => { console.error(err); checkAllLoaded(); });
    unsubscribers.push(unsubFeedbacks);
    
    return () => { unsubscribers.forEach(unsub => unsub()); };
  }, [teamId]);

  // Carica lat/lon del track per l'evento attivo OGGI (meteo radar + live timing).
  // Logica: priorità a evento il cui range [startDate..endDate] contiene oggi (così i widget
  // restano visibili tutto il giorno indipendentemente dall'orario delle singole sessioni);
  // fallback alla prossima sessione futura per coprire giornate non-evento con sessioni isolate.
  useEffect(() => {
    if (!allEvents.length) return;

    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const endOfToday   = new Date(); endOfToday.setHours(23, 59, 59, 999);
    const startOfTodayMs = startOfToday.getTime();
    const endOfTodayMs   = endOfToday.getTime();

    // 1. Evento attivo oggi: il range [startDate..endDate] interseca la giornata corrente
    let event = allEvents.find(e => {
      const start = e.startDate?.toDate?.()?.getTime();
      const end   = e.endDate?.toDate?.()?.getTime();
      if (!start || !end) return false;
      return start <= endOfTodayMs && end >= startOfTodayMs;
    });

    // 2. Fallback: prossima sessione futura (vecchia logica)
    if (!event && allSessions.length) {
      const nowSec = Timestamp.now().seconds;
      const nextSession = allSessions.find(s => {
        if (!s.startTime) return false;
        return s.startTime.seconds + (s.duration || 0) * 60 > nowSec;
      });
      if (nextSession) {
        event = allEvents.find(e => e.id === nextSession.eventId);
      }
    }

    if (!event) { setCurrentEventTrack(null); return; }

    const liveTimingUrl = event.liveTimingUrl || '';

    const loadTrack = async () => {
      let lat = null, lon = null, nome = event.trackName || '';

      // 1. Prova dal documento tracks (lat/lon salvati dopo upload GPS nuovo)
      if (event.trackId) {
        try {
          const snap = await getDoc(doc(db, 'tracks', event.trackId));
          if (snap.exists()) {
            const d = snap.data();
            nome = d.nome || nome;
            lat  = d.lat  || null;
            lon  = d.lon  || null;
          }
        } catch (_) {}
      }

      // 2. Fallback: calcola centroide da re_tracks.gps_trace (piste già caricate)
      if ((!lat || !lon) && nome) {
        try {
          const { trackNameToSlug } = await import('../utils/reTracksService');
          const slug = trackNameToSlug(nome);
          const reSnap = await getDoc(doc(db, 're_tracks', slug));
          if (reSnap.exists()) {
            const gps = reSnap.data().gps_trace || [];
            if (gps.length > 0) {
              lat = gps.reduce((s, p) => s + p.lat, 0) / gps.length;
              lon = gps.reduce((s, p) => s + p.lon, 0) / gps.length;
              // Salva per la prossima volta nel documento tracks
              if (event.trackId) {
                const { updateDoc } = await import('firebase/firestore');
                updateDoc(doc(db, 'tracks', event.trackId), {
                  lat: Math.round(lat * 100000) / 100000,
                  lon: Math.round(lon * 100000) / 100000,
                }).catch(() => {});
              }
            }
          }
        } catch (_) {}
      }

      setCurrentEventTrack({ lat, lon, nome, liveTimingUrl });
    };

    loadTrack();
  }, [allEvents, allSessions]);

  // Funzioni (invariate)
  const isManager = userRole === 'admin' || userRole === 'Engineer';
  
  const teamName = teamDetails?.nomeTeam || t('your_team');

  const eventsMap = useMemo(() => {
    return allEvents.reduce((acc, event) => {
      acc[event.id] = event;
      return acc;
    }, {});
  }, [allEvents]);
  
  // Layout (invariati)
  const widgetStyle = {
    p: 2,
    minHeight: 200,
    height: '100%',
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    elevation: 0,
  };
  const widgetBoxStyle = {
    width: { xs: '100%', md: 'calc(50% - 12px)' }, 
    mb: 3, 
  };
  
  // --- MODIFICA: Determina il logo da mostrare ---
  const logoToShow = mode === 'light' 
    ? (teamLogos?.light || teamLogos?.dark) // Tema chiaro: usa logo chiaro, fallback su scuro
    : (teamLogos?.dark || teamLogos?.light); // Tema scuro: usa logo scuro, fallback su chiaro
  // --- FINE MODIFICA ---

  return (
    <>
      {/* --- MODIFICA QUI --- */}
      {/* 1. Header Visivo (Ora usa 'logoToShow') */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          textAlign: 'center', 
          backgroundColor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          minHeight: 150, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
          {/* --- MODIFICA: Logica di visualizzazione del logo --- */}
          {logoToShow ? (
            // CASO 1: Logo Caricato (chiaro o scuro)
            <Box 
              component="img" 
              src={logoToShow} 
              alt={`${teamName} Logo`} 
              sx={{ 
                maxHeight: 120, // Altezza logo
                width: 'auto',
                maxWidth: '90%',
                objectFit: 'contain',
                mb: 1 
              }} 
            />
          ) : (
            // CASO 2: Fallback (Nessun Logo)
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                {teamName}
            </Typography>
          )}
          {/* --- FINE MODIFICA --- */}
          
          {/* Sottotitolo (invariato) */}
          <Typography variant="h6" color="text.secondary">
              {t('header_title')}
          </Typography>
      </Paper>
      {/* --- FINE MODIFICA --- */}


      {isLoading ? <CircularProgress /> : (
        <Box> 
        
          {/* 2. Next Session Widget */}
          <Box sx={{ width: '100%', mb: 3 }}>
            <Paper elevation={0} sx={{ p: 3, textAlign: 'center', height: '100%', border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <NextSessionWidget 
                sessions={allSessions.filter(s => {
                    const nowSec = Timestamp.now().seconds;
                    if (s.startTime && s.duration) {
                        const bufferSec = (teamDetails?.pitLaneBuffer || 0) * 60;
                        const effectiveEndSec = s.startTime.seconds + (s.duration * 60) + bufferSec;
                        return effectiveEndSec > nowSec;
                    }
                    return s.startTime.seconds > nowSec;
                })} 
                t={t} 
                teamDetails={teamDetails} 
              />
            </Paper>
          </Box>
          
          {/* 3. Pit Lane Widget */}
          <PitLaneAccessWidget
            sessions={allSessions}
            setups={allSetups}
            vehiclesMap={vehiclesMap}
            userRole={userRole}
            userId={userId}
            assignments={allAssignments}
            t={t} 
            teamDetails={teamDetails} 
          />


          {/* 3b. Live Timing + Meteo Radar — due colonne */}
          {(currentEventTrack?.liveTimingUrl || (currentEventTrack?.lat && currentEventTrack?.lon)) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <LiveTimingWidget
                url={currentEventTrack?.liveTimingUrl}
                eventName={allEvents.find(e => allSessions.find(s => s.eventId === e.id && s.startTime?.seconds + (s.duration||0)*60 > Timestamp.now().seconds))?.eventName}
                t={t}
              />
              <WeatherWidget
                lat={currentEventTrack?.lat}
                lon={currentEventTrack?.lon}
                trackName={currentEventTrack?.nome}
                t={t}
              />
            </Box>
          )}

          {/* 4. CONTENITORE WIDGET SECONDARI (Invariato) */}
          <Box 
            sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}
          >
          
            {/* Pending Event Checklists Widget */}
            {(isManager || userRole === 'Mechanic') && (
              <Box sx={widgetBoxStyle}>
                <Paper sx={widgetStyle}>
                  <Typography variant="h6" gutterBottom>{t('widget_pending_event_checklists')}</Typography>
                  <PendingEventChecklistsWidget
                    checklists={allEventChecklists}
                    eventsMap={eventsMap}
                    t={t} 
                  />
                </Paper>
              </Box>
            )}

            {/* Unread Driver Feedbacks Widget */}
            {(isManager && allUnreadFeedbacks.length > 0) && (
              <Box sx={widgetBoxStyle}>
                <Paper sx={{...widgetStyle, borderColor: 'secondary.main', borderWidth: 2}}>
                  <Typography variant="h6" color="secondary.main" gutterBottom>
                    <CampaignIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> 
                    {t('unread_feedbacks_title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('unread_feedbacks_subtitle')}
                  </Typography>
                  <UnreadFeedbackWidget
                    feedbacks={allUnreadFeedbacks}
                    vehiclesMap={vehiclesMap}
                    userRole={userRole}
                    assignments={allAssignments}
                    userId={userId}
                    t={t} 
                  />
                </Paper>
              </Box>
            )}

            {/* Open Issues Log Widget */}
            {(isManager || userRole === 'Mechanic') && (
              <Box sx={widgetBoxStyle}> 
                <Paper sx={widgetStyle}>
                  <Typography variant="h6" gutterBottom>{t('widget_open_issues')}</Typography>
                  <IssueLogWidget
                    issues={allIssues} vehicles={vehiclesMap}
                    assignments={allAssignments} userRole={userRole} userId={userId}
                    t={t} 
                  />
                </Paper>
              </Box>
            )}

            {/* Event Logistics Setup Widget */}
            {(isManager) && ( 
              <Box sx={widgetBoxStyle}>
                <Paper sx={widgetStyle}>
                  <Typography variant="h6" gutterBottom>{t('widget_event_logistics')}</Typography>
                  <UpcomingLogisticsWidget
                    events={allEvents}
                    t={t} 
                  />
                </Paper>
              </Box>
            )}

            {/* QuickLifingWidget (Presuppone traduzione interna o nessuna) */}
            {(isManager || userRole === 'Mechanic') && (
              <Box sx={widgetBoxStyle}>
                <QuickLifingWidget 
                  teamId={teamId}
                  vehicles={vehiclesList}
                />
              </Box>
            )}
            
            {/* Incomplete Session Checklists */}
            {(isManager || userRole === 'Mechanic') && (
              <Box sx={widgetBoxStyle}>
                <Paper sx={widgetStyle}>
                  <Typography variant="h6" gutterBottom>{t('widget_incomplete_past_sessions')}</Typography>
                  <ChecklistWarningsWidget
                    sessions={allSessions} 
                    setups={allSetups}
                    checklists={allChecklists}
                    assignments={allAssignments} 
                    userRole={userRole} 
                    userId={userId}
                    t={t} 
                  />
                </Paper>
              </Box>
            )}

            {/* Component Wear Alerts Widget */}
            {(isManager || userRole === 'Mechanic') && (
              <Box sx={widgetBoxStyle}>
                <Paper sx={widgetStyle}>
                  <Typography variant="h6" gutterBottom>{t('widget_component_wear')}</Typography>
                  <LifingWarningsWidget
                    components={allComponents} vehicles={vehiclesMap} assignments={allAssignments}
                    userRole={userRole} userId={userId}
                    t={t} 
                  />
                </Paper>
              </Box>
            )}
            
          </Box> 
        </Box>
      )}
    </>
  );
}

export default DashboardHome;